import { colorPalette, fundamentalSizeMultiplier } from '../drawing/constants';
import { ShapeKind, DrawingMode } from '../drawing/types';
import { IconImageColorFilterProps } from '../components/common/IconImage';
import { Color, Rgba } from '../types/color';
import { LineWidth } from '../types/lineWidth';
import { FreehandPenWidth } from '../types/freehandPenWidth';

export const getShapeKindIconName = (shapeKind: ShapeKind): string => {
  switch (shapeKind) {
    case ShapeKind.Rect:
      return 'square_02';
    case ShapeKind.Square:
      return 'square_01';
    case ShapeKind.Oval:
      return 'circle_02';
    case ShapeKind.Circle:
      return 'circle_01';
    case ShapeKind.EquilateralTriangle:
      return 'triangle_01';
    case ShapeKind.Triangle:
      return 'triangle_02';
    case ShapeKind.StraightLine:
      return 'two_points';
    case ShapeKind.AxisAlignedStraightLine:
      return 'line_straight';
    case ShapeKind.Polyline:
      return 'polygon';
    case ShapeKind.Polygon:
      return 'polygon';
    default:
      return '';
  }
};

export const getColorFilter = (color: Color): IconImageColorFilterProps => {
  const colorValue = colorPalette[color];
  if (colorValue === undefined) {
    throw new Error('Undefined color.');
  }
  return {
    useFilter: true,
    filterRed: colorValue.red / 255,
    filterGreen: colorValue.green / 255,
    filterBlue: colorValue.blue / 255,
    filterAlpha: colorValue.alpha,
  };
};

export const convertDrawingModeToShapeKind = (shapeKind: ShapeKind): DrawingMode => {
  switch (shapeKind) {
    case ShapeKind.Rect:
      return DrawingMode.RectDrawing;
    case ShapeKind.Square:
      return DrawingMode.SquareDrawing;
    case ShapeKind.Oval:
      return DrawingMode.OvalDrawing;
    case ShapeKind.Circle:
      return DrawingMode.CircleDrawing;
    case ShapeKind.Triangle:
      return DrawingMode.TriangleDrawing;
    case ShapeKind.EquilateralTriangle:
      return DrawingMode.EquilateralTriangleDrawing;
    case ShapeKind.Sticky:
      return DrawingMode.StickyDrawing;
    case ShapeKind.Balloon:
      return DrawingMode.BalloonDrawing;
    case ShapeKind.StraightLine:
      return DrawingMode.StraightLineDrawing;
    case ShapeKind.AxisAlignedStraightLine:
      return DrawingMode.AxisAlignedStraightLineDrawing;
    case ShapeKind.Polyline:
      return DrawingMode.MultiVertexDrawing;
    case ShapeKind.Polygon:
      return DrawingMode.MultiVertexDrawing;
    case ShapeKind.ImageFile:
      return DrawingMode.ImagePasting;
    case ShapeKind.MultiroleLink:
      return DrawingMode.MultiroleLinkPasting;
    case ShapeKind.Stamp:
      return DrawingMode.Stamp;
    case ShapeKind.LearningPenRect:
      return DrawingMode.LearningPenRect;
    default:
      return DrawingMode.Selection;
  }
};

export const convertColorToColorString = (color: Color, alphaOverride?: number): string => {
  if (color === Color.Transparent) {
    return 'transparent';
  }
  const colorValue = colorPalette[color];
  if (colorValue === undefined) {
    throw new Error('Undefined color.');
  }
  return convertRGBAtoColorString(colorValue, alphaOverride);
};

export const convertRGBAtoColorString = (rgba: Rgba, alphaOverride?: number): string => {
  return `rgba(
    ${rgba.red},
    ${rgba.green},
    ${rgba.blue},
    ${alphaOverride !== undefined ? alphaOverride : rgba.alpha})`;
};

export const isTransparentColor = (color: Color): boolean => {
  switch (color) {
    case Color.Transparent:
    case Color.RedTransparent:
    case Color.BlueTransparent:
    case Color.YellowTransparent:
    case Color.GreenTransparent:
    case Color.BlackTransparent:
    case Color.WhiteTransparent:
      return true;
    default:
      return false;
  }
};

export const convertLineWidthToValue = (lineWidth: LineWidth): number => {
  switch (lineWidth) {
    case LineWidth.one:
      return 1 * fundamentalSizeMultiplier;
    case LineWidth.two:
      return 10 * fundamentalSizeMultiplier;
    case LineWidth.three:
      return 20 * fundamentalSizeMultiplier;
    case LineWidth.four:
      return 30 * fundamentalSizeMultiplier;
    case LineWidth.five:
      return 40 * fundamentalSizeMultiplier;
  }
};

export const convertLearingPenWidthToValue = (lineWidth: LineWidth): number => {
  switch (lineWidth) {
    case LineWidth.one:
      return 20 * fundamentalSizeMultiplier;
    case LineWidth.two:
      return 40 * fundamentalSizeMultiplier;
    case LineWidth.three:
      return 60 * fundamentalSizeMultiplier;
    case LineWidth.four:
      return 80 * fundamentalSizeMultiplier;
    case LineWidth.five:
      return 100 * fundamentalSizeMultiplier;
  }
};

export const convertFreehandPenWidthToValue = (freehandPenWidth: FreehandPenWidth): number => {
  switch (freehandPenWidth) {
    case FreehandPenWidth.one:
      return 1 * fundamentalSizeMultiplier;
    case FreehandPenWidth.two:
      return 10 * fundamentalSizeMultiplier;
    case FreehandPenWidth.three:
      return 20 * fundamentalSizeMultiplier;
    case FreehandPenWidth.four:
      return 30 * fundamentalSizeMultiplier;
    case FreehandPenWidth.five:
      return 40 * fundamentalSizeMultiplier;
  }
};
